import axios, { AxiosError } from 'axios';
import { ApiResponse } from '../types/plant';

// In production, requests go to /api (same origin, proxied by Cloudflare Pages _redirects).
// In development, Vite's dev server proxies /api to the local backend.
const API_BASE_URL = import.meta.env.VITE_API_URL || '';

// Create axios instance with default config
export const api = axios.create({
  baseURL: `${API_BASE_URL}/api`,
  withCredentials: true, // Include cookies for session management
  headers: {
    'Content-Type': 'application/json',
  },
});

// Store CSRF token
let csrfToken: string | null = null;

// Function to get CSRF token from cookie
function getCSRFTokenFromCookie(): string | null {
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'csrf-token') {
      return value;
    }
  }
  return null;
}

// Request interceptor to add auth token and CSRF token
api.interceptors.request.use(async config => {
  const token = localStorage.getItem('sessionToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Add CSRF token for state-changing requests
  const safeMethods = ['GET', 'HEAD', 'OPTIONS'];
  if (!safeMethods.includes(config.method?.toUpperCase() || '')) {
    // Get CSRF token from cookie or fetch it
    if (!csrfToken) {
      csrfToken = getCSRFTokenFromCookie();
    }

    if (csrfToken) {
      config.headers['x-csrf-token'] = csrfToken;
    }
  }

  return config;
});

// Response interceptor to handle auth errors and CSRF token refresh
api.interceptors.response.use(
  response => {
    // Update CSRF token from cookie after successful requests
    const newToken = getCSRFTokenFromCookie();
    if (newToken) {
      csrfToken = newToken;
    }
    return response;
  },
  async (error: AxiosError) => {
    if (error.response?.status === 401) {
      // Clear token and redirect to login
      localStorage.removeItem('sessionToken');
      csrfToken = null;
      window.location.href = '/login';
    } else if (
      error.response?.status === 403 &&
      (error.response?.data as ApiResponse<unknown>)?.error?.includes('CSRF')
    ) {
      // CSRF token invalid, try to refresh it
      try {
        const response = await api.get('/auth/csrf-token');
        csrfToken = response.data.data.csrfToken;
        // Retry the original request
        const originalRequest = error.config;
        if (originalRequest) {
          originalRequest.headers['x-csrf-token'] = csrfToken;
          return api(originalRequest);
        }
      } catch {
        // Unable to refresh CSRF token, redirect to login
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Waitlist API
export interface AddToWaitlistRequest {
  email: string;
  source?: string;
}

export interface WaitlistResponse {
  success: boolean;
  data?: {
    id?: string;
    email?: string;
    source?: string;
    createdAt?: string;
    message?: string;
  };
  error?: string;
  details?: string;
}

export const addToWaitlist = async (data: AddToWaitlistRequest): Promise<WaitlistResponse> => {
  const response = await api.post<WaitlistResponse>('/waitlist', data);
  return response.data;
};
